/**
 * NoticePanel — the mock's `.notice`: the « À savoir sur ces chiffres » block.
 *
 * Lavender on violet (`#F3EEFD` / `#E2D7FB`), not amber. The caveats are not
 * warnings about a malfunction, they are the reading instructions for the numbers
 * directly below — an amber panel makes a correctly-working dashboard look broken,
 * and the user learns to dismiss it.
 *
 * Renders a LIST, one `<li>` per caveat: these are independent statements, and a
 * paragraph of them run together is read as one and skipped. A single caveat still
 * renders as a list of one — the count is data, not a layout choice.
 */

import { Box, Typography } from '@mui/material';
import { CARD, VIZ } from './tokens';

export interface NoticePanelProps {
  title: string;
  lines: React.ReactNode[];
  sx?: object;
}

export default function NoticePanel({ title, lines, sx }: NoticePanelProps) {
  if (lines.length === 0) return null;
  return (
    <Box
      role="note"
      sx={{
        display: 'flex',
        gap: 1.75,
        alignItems: 'flex-start',
        bgcolor: '#F3EEFD',
        border: '1px solid #E2D7FB',
        borderRadius: `${CARD.noticeRadius}px`,
        p: '16px 20px',
        ...sx,
      }}
    >
      {/* A PLAIN `<svg>`, not `Box component="svg"`: `Box` consumes `width`/`height`
          as SIZING system props, and a unitless string ("20") is invalid CSS, so it
          is dropped — leaving the SVG to fill its container. That blew the icon up to
          the full width of the panel and squeezed the text into a column (2026-10-08).
          `ChartCard`'s glyphs and `StatTile`'s ring are plain `<svg>` for this reason. */}
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke={VIZ.violet}
        strokeWidth={2}
        strokeLinecap="round"
        aria-hidden="true"
        style={{ flex: 'none', marginTop: 1, display: 'block' }}
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5M12 8h.01" />
      </svg>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="strong" sx={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#2A1A5E', mb: 0.75 }}>
          {title}
        </Typography>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2.25,
            fontSize: 13.5,
            lineHeight: 1.6,
            color: '#4A4458',
            '& li + li': { mt: 0.25 },
          }}
        >
          {lines.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
